import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import {
  InterestFilterDashboard as QueryRes,
  InterestFilterDashboardVariables as QueryVars,
} from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner } from "components/graphql-views";
import { deleteMutation, insertMutation, query } from "./queries";
import { groupedInterestFilters } from "./utils";

const FilterLabel = ({ heading, text }: { heading: boolean; text: string }) =>
  (heading ? <b style={{ color: "black" }}>{text}</b> : <span>{text}</span>);

const FilterComponent: React.FC<{ interestListId: string }> = ({ interestListId }) => {
  const queryOpts = { variables: { interestListId } };
  const { data, loading, error } = useQuery<QueryRes, QueryVars>(query, queryOpts);
  const [insertFilter] = useMutation(insertMutation);
  const [deleteFilter] = useMutation(deleteMutation);
  const { openSnackbar } = useSnackbar();
  const handleUpdateChange = (filterId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const mutationArgs = { variables: { interestListId, filterId } };
    const filterToApply = (event.target.checked) ? insertFilter : deleteFilter;
    filterToApply({
      ...mutationArgs,
      refetchQueries: [{ query, variables: { interestListId } }],
    })
      .then(() => openSnackbar({ message: "Filter updated", status: "success" }))
      .catch((promiseError) => {
        logger.sendError(promiseError);
        openSnackbar({ message: "Failed to update filter", status: "error" });
      });
  };

  if (error) return <div>Error loading filter data</div>;
  if (loading) return <LoadingSpinner />;

  const filterGroups = groupedInterestFilters(data?.interestFilters || []);
  const listFilters = data?.interestList?.interestFilters || [];
  const filterIds = listFilters.map((filter) => filter.id);

  return (
    <Grid container spacing={3}>
      {filterGroups.map((group, i) => (
        <Grid key={i}>
          {group.map(({ id, label, name, type }) => (
            <FormControlLabel
              key={id}
              style={{ display: "block", marginBottom: -6 }}
              control={
                <Checkbox
                  checked={filterIds.includes(id)}
                  // Semantic filter name (e.g. last_status_change), not the UUID id.
                  name={name}
                  onChange={handleUpdateChange(id)}
                />
              }
              label={<FilterLabel text={label} heading={type === "heading"} />}
            />
          ))}
        </Grid>
      ))}
    </Grid>
  );
};

export default FilterComponent;
